import React, { useMemo, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';

import { useCurrentUser, useForumPost } from '../../../hooks';
import { new_session_path, subscribe_one_path } from '../../../routes';
import { ForumPost, NewForumPost } from '../../../utils/backend-api/forumPosts';
import { ProfileModal } from '../../shared';
import ForumPanel from '../ForumPanel';
import CreatedCommentSuccess from './CreatedCommentSuccess';
import NewForumPostForm, {
  QUICK_TAG_FUNDING,
  QUICK_TAG_JOBS,
  QUICK_TAG_LAUNCH,
} from './NewForumPostForm';
import styles from './styles.module.scss';

const INITIAL_VALUES: NewForumPost = {
  title: '',
  body: '',
  external_url: '',
  manual_entities: [],
  email_digest_enabled: true,
  job_tag: false,
  product_tag: false,
  funding_tag: false,
  is_anonymous: false,
};

const LOGGED_OUT_MODAL_CONTENT = {
  header: 'Subscribe to The Information',
  subheader: 'Become a subscriber to publish a post',
  linkName: 'GET STARTED',
  link: subscribe_one_path,
  benefits: ['Start a discussion', 'Network with other subscribers', 'Send direct messages'],
};

function getInitialValues(forumPost: ForumPost, id?: string) {
  if (typeof id !== 'undefined') {
    const tagList = forumPost?.manual_entities?.map((e) => e.label) || [];
    return forumPost
      ? {
          id: forumPost?.id,
          title: forumPost?.title,
          body: forumPost?.comment?.body,
          external_url: forumPost?.external_url,
          manual_entities: forumPost?.manual_entities,
          email_digest_enabled: forumPost?.email_digest_enabled,
          job_tag: tagList.includes(QUICK_TAG_JOBS),
          product_tag: tagList.includes(QUICK_TAG_LAUNCH),
          funding_tag: tagList.includes(QUICK_TAG_FUNDING),
        }
      : undefined;
  }
  return INITIAL_VALUES;
}

export default function EditForumPostPage() {
  const params = useParams();
  const { currentUser } = useCurrentUser();
  const location = useLocation();
  const { forumPost } = useForumPost(params.id);
  const [createdForumPost, setCreatedForumPost] = useState<ForumPost | undefined>();
  const initialValues = useMemo(
    () => getInitialValues(forumPost, params.id),
    [forumPost, params.id]
  );

  const isAdmin = currentUser?.administrator?.is_active;
  const isManaging = isAdmin && !!params.id && forumPost?.comment?.user?.id !== currentUser.id;

  return (
    <div className={styles.NewNetworkPostPage}>
      <div className="container container-padded">
        <ForumPanel>
          {!!createdForumPost && <CreatedCommentSuccess forumPost={createdForumPost} />}
          {/* Keep in DOM in case user continues steps in EnableProfileModal */}
          <div style={{ display: createdForumPost ? 'none' : undefined }}>
            <h1 className={styles.heading}>
              {isManaging ? (
                'Manage conversation'
              ) : (
                <>{params.id ? 'Edit conversation' : 'Start a new conversation'}</>
              )}
            </h1>
            {!!initialValues && (
              <NewForumPostForm
                initialValues={initialValues}
                isManaging={isManaging}
                onCreateSuccess={(forumPost) => setCreatedForumPost(forumPost)}
              />
            )}
          </div>
        </ForumPanel>
      </div>
      {(!currentUser || currentUser?.is_free_edition_user) && (
        <ProfileModal
          modalContent={LOGGED_OUT_MODAL_CONTENT}
          currentUserLoggedIn={!!currentUser}
          newSessionUrl={new_session_path({ return_to: location.pathname })}
        />
      )}
    </div>
  );
}
